import { Button, Dialog, Input, RadioBox } from 'components/ui';
import { useMarketplaceConfig } from 'hooks/use-marketplace-config';
import {
  getListOfNetworks,
  hasValue,
  IFilter,
  inputType,
  listingStatusItems,
  normaliseFilterArrays,
  listingVisibilityFilterItems,
  priceType,
  productType,
  soldStatus,
  auctionStatus,
  states,
  stockCategory,
  toFilterArray,
  types,
  useBadgeCount,
} from 'lib/marketplace-utils';
import { ChangeEvent, FC, useEffect, useState } from 'react';
import styled from 'styled-components';
import { globalTheme } from 'styles/global-theme';
import { MarketplaceFilterCardEnum } from '../marketplace/filters/helpers';

export interface MarketPlaceFilterDialogProps {
  onDismiss?: VoidFunction;
  isOpen: boolean;
  clearAllFilters: VoidFunction;
  instance: MarketplaceFilterCardEnum;
  initialFilters: Record<string, any>;
  onApplyFilters: (filters: Record<string, any>) => void;
}

interface StyledInputProps {
  hasValue: boolean;
}

const Label = styled.span`
  font-weight: 700;
  font-size: 14px;
`;
const FilterContainer = styled.div`
  display: flex;
  flex-direction: column;

  gap: 10px;
`;
const FilterWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  text-align: center;
`;

const CInput = styled(Input)<StyledInputProps>`
  width: 100px;
  label {
    color: ${(props) => (props.hasValue ? globalTheme.colors.blue : '')};
  }
  div {
    border-color: ${(props) => (props.hasValue ? globalTheme.colors.blue : '')};
  }
  &:focus-within {
    label {
      color: ${globalTheme.colors.blue};
    }
    div {
      border-color: ${globalTheme.colors.blue};
    }
  }
`;
const FlexContainer = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: center;
`;

const SearchInput = styled(Input)<StyledInputProps>`
  label {
    color: ${(props) => (props.hasValue ? globalTheme.colors.blue : '')};
  }
  div {
    border-color: ${(props) => (props.hasValue ? globalTheme.colors.blue : '')};
  }
  &:focus-within {
    label {
      color: ${globalTheme.colors.blue};
    }
    div {
      border-color: ${globalTheme.colors.blue};
    }
  }
`;

const CRadioBox = styled(RadioBox)`
  > label {
    height: 30px;
    padding: 16px;
    white-space: nowrap;
    cursor: pointer;
  }
`;

const ActionContainer = styled.div`
  display: flex;
  gap: ${globalTheme.space[2]};
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

export const MarketPlaceFilterDialog: FC<MarketPlaceFilterDialogProps> = ({
  onDismiss = () => undefined,
  isOpen,
  clearAllFilters,
  initialFilters,
  onApplyFilters,
  instance,
}) => {
  const [modalFilter, setModalFilter] = useState<IFilter>(() =>
    normaliseFilterArrays(initialFilters)
  );
  const { data: marketplaceConfig, isLoading: isMarketplaceConfigLoading } =
    useMarketplaceConfig();

  const networkItems =
    !isMarketplaceConfigLoading &&
    marketplaceConfig &&
    !!marketplaceConfig.networks.length
      ? getListOfNetworks(marketplaceConfig).map((item) => ({
          id: item.id,
          value: item.name,
        }))
      : [];

  useEffect(() => {
    setModalFilter(normaliseFilterArrays(initialFilters));
  }, [initialFilters]);

  const clearModalFilter = () => {
    setModalFilter({});
    clearAllFilters();
  };

  const actions = (
    <ActionContainer>
      <Button
        size='small'
        iconRight='CancelIcon'
        disabled={useBadgeCount(modalFilter) === 0}
        onClick={clearModalFilter}
      >
        Clear
      </Button>
      <Button
        size='small'
        onClick={() => {
          onApplyFilters(modalFilter);
          onDismiss();
        }}
      >
        Filter ({useBadgeCount(modalFilter)})
      </Button>
    </ActionContainer>
  );

  const handleValueChange = (
    event: ChangeEvent<HTMLInputElement | any>,
    type: any
  ) => {
    const { name, value, checked } = event.target;

    if (type === inputType.INPUT) {
      setModalFilter((prevFormData) => {
        const updatedFilter: IFilter = {
          ...prevFormData,
          [name]: value,
        };
        if (value === '' || value === undefined) {
          delete updatedFilter[name as keyof IFilter];
        }
        return updatedFilter;
      });
    }

    if (type === inputType.RADIO) {
      setModalFilter((prevFormData) => {
        let updatedFilter: IFilter = {
          ...prevFormData,
          [name]: value,
        };
        if (
          modalFilter[name as keyof IFilter] ===
          updatedFilter[name as keyof IFilter]
        ) {
          delete updatedFilter[name as keyof IFilter];
        }

        if (value === '' || value === undefined) {
          delete updatedFilter[name as keyof IFilter];
        }
        return updatedFilter;
      });
    }

    if (type === inputType.CHECKBOX) {
      setModalFilter((prevFormData) => {
        const prevValues = toFilterArray(prevFormData[name as keyof IFilter]);
        const updatedValues = checked
          ? [...prevValues.filter((item) => item !== value), value]
          : prevValues.filter((item) => item !== value);

        const updatedFilter: IFilter = {
          ...prevFormData,
          [name]: updatedValues,
        };

        if (updatedValues.length === 0) {
          delete updatedFilter[name as keyof IFilter];
        }

        return updatedFilter;
      });
    }
  };

  return (
    <Dialog
      dataCypress={'marketplaceFilterDialog'}
      title='Filters'
      actions={actions}
      onDismiss={() => {
        setModalFilter(normaliseFilterArrays(initialFilters));
        onDismiss();
      }}
      isOpen={isOpen}
    >
      <FilterWrapper>
        {/* livestock type */}
        <FilterContainer>
          <Label>Select type of livestock</Label>
          <FlexContainer>
            {productType.map((product, idx) => (
              <CRadioBox
                key={idx}
                label={product.title}
                name='product'
                type={'checkbox'}
                checked={modalFilter?.product === product.id}
                data-cy='productType'
                value={product.id}
                onChange={(e) => handleValueChange(e, inputType.RADIO)}
              />
            ))}
          </FlexContainer>
        </FilterContainer>

        {/* Network */}
        {networkItems && !!networkItems.length && (
          <FilterContainer>
            <Label>Network(s)</Label>
            <ButtonContainer>
              <CRadioBox
                key='networks-only-listings'
                label='Networks only listings'
                type={'checkbox'}
                checked={
                  Object.keys(modalFilter).length &&
                  modalFilter.networks?.includes('all')
                    ? true
                    : false
                }
                name='networks'
                data-cy='networksOnlyListings'
                value='all'
                onChange={(e) => {
                  return handleValueChange(e, inputType.CHECKBOX);
                }}
              />
              {networkItems.map((network, idx) => {
                return (
                  <CRadioBox
                    key={idx}
                    label={network.value}
                    type={'checkbox'}
                    checked={
                      Object.keys(modalFilter).length &&
                      modalFilter.networks?.includes(network.id)
                        ? true
                        : false
                    }
                    name='networks'
                    data-cy='networks'
                    value={network.id}
                    onChange={(e) => {
                      return handleValueChange(e, inputType.CHECKBOX);
                    }}
                  />
                );
              })}
            </ButtonContainer>
          </FilterContainer>
        )}

        {/* Public status */}
        {instance !== MarketplaceFilterCardEnum.MARKETPLACE && (
          <FilterContainer>
            <Label>Public status</Label>
            <ButtonContainer>
              {listingStatusItems.map((status, idx) => {
                return (
                  <CRadioBox
                    key={idx}
                    label={status.value}
                    type={'checkbox'}
                    checked={
                      Object.keys(listingStatusItems).length &&
                      modalFilter.public_status?.includes(status.id)
                        ? true
                        : false
                    }
                    name='public_status'
                    data-cy='publicStatus'
                    value={status.id}
                    onChange={(e) => {
                      return handleValueChange(e, inputType.CHECKBOX);
                    }}
                  />
                );
              })}
            </ButtonContainer>
          </FilterContainer>
        )}

        {/* Visibility */}
        {instance !== MarketplaceFilterCardEnum.MARKETPLACE && (
          <FilterContainer>
            <Label>Visibility</Label>
            <ButtonContainer>
              {listingVisibilityFilterItems.map((visibility, idx) => {
                return (
                  <CRadioBox
                    key={idx}
                    label={visibility.value}
                    type={'checkbox'}
                    checked={
                      Object.keys(listingVisibilityFilterItems).length &&
                      modalFilter.visibility?.includes(visibility.id)
                        ? true
                        : false
                    }
                    name='visibility'
                    data-cy='visibility'
                    value={visibility.id}
                    onChange={(e) => {
                      return handleValueChange(e, inputType.CHECKBOX);
                    }}
                  />
                );
              })}
            </ButtonContainer>
          </FilterContainer>
        )}

        {/* state */}
        {instance === MarketplaceFilterCardEnum.MARKETPLACE && (
          <FilterContainer>
            <Label>State</Label>
            <ButtonContainer>
              {states.map((state, idx) => {
                return (
                  <CRadioBox
                    key={idx}
                    label={state}
                    type={'checkbox'}
                    checked={
                      Object.keys(modalFilter).length &&
                      modalFilter.state?.includes(state)
                        ? true
                        : false
                    }
                    name='state'
                    data-cy='state'
                    value={state}
                    onChange={(e) => handleValueChange(e, inputType.CHECKBOX)}
                  />
                );
              })}
            </ButtonContainer>
          </FilterContainer>
        )}

        {/* stock Category */}
        {instance === MarketplaceFilterCardEnum.MARKETPLACE && (
          <FilterContainer>
            <Label>Stock Category</Label>
            <ButtonContainer>
              {stockCategory.map((stock, idx) => (
                <CRadioBox
                  key={idx}
                  label={stock}
                  type={'checkbox'}
                  checked={
                    Object.keys(modalFilter).length &&
                    modalFilter.stockCat?.includes(stock)
                      ? true
                      : false
                  }
                  name='stockCat'
                  data-cy='stockCat'
                  value={stock}
                  onChange={(e) => handleValueChange(e, inputType.CHECKBOX)}
                />
              ))}
            </ButtonContainer>
          </FilterContainer>
        )}

        {/** Filled Status*/}
        <FilterContainer>
          <Label>Status</Label>
          <ButtonContainer>
            {soldStatus.map((status, idx) => {
              return (
                <CRadioBox
                  key={idx}
                  label={status.label}
                  type={'checkbox'}
                  // An absent filter is "All", whose value is ''.
                  checked={(modalFilter.sold ?? '') === status.value}
                  name='sold'
                  data-cy='soldStatus'
                  value={status.value}
                  onChange={(e) => handleValueChange(e, inputType.RADIO)}
                />
              );
            })}
          </ButtonContainer>
        </FilterContainer>

        {/** Auction */}
        <FilterContainer>
          <Label>Auction</Label>
          <ButtonContainer>
            {auctionStatus.map((status, idx) => {
              return (
                <CRadioBox
                  key={idx}
                  label={status.label}
                  type={'checkbox'}
                  checked={(modalFilter.auction ?? '') === status.value}
                  name='auction'
                  data-cy='auctionStatus'
                  value={status.value}
                  onChange={(e) => handleValueChange(e, inputType.RADIO)}
                />
              );
            })}
          </ButtonContainer>
        </FilterContainer>

        {/* transaction type */}
        <FilterContainer>
          <Label>Offer type</Label>
          <FlexContainer>
            {types.map((type, idx) => (
              <CRadioBox
                key={idx}
                label={type.label}
                name='type'
                type={'checkbox'}
                checked={modalFilter?.type === type.value}
                data-cy='transactionType'
                value={type.value}
                onChange={(e) => handleValueChange(e, inputType.RADIO)}
              />
            ))}
          </FlexContainer>
        </FilterContainer>

        {/* quantity */}
        {instance === MarketplaceFilterCardEnum.MARKETPLACE && (
          <FilterContainer>
            <Label>Quantity</Label>
            <FlexContainer>
              <CInput
                type='number'
                name='quantityMin'
                label='Min'
                hasValue={hasValue(modalFilter?.quantityMin)}
                value={
                  Object.keys(modalFilter).length
                    ? modalFilter?.quantityMin
                    : ''
                }
                onChange={(e) => handleValueChange(e, inputType.INPUT)}
              />
              <CInput
                type='number'
                name='quantityMax'
                label='Max'
                hasValue={hasValue(modalFilter?.quantityMax)}
                value={
                  Object.keys(modalFilter).length
                    ? modalFilter?.quantityMax
                    : ''
                }
                onChange={(e) => handleValueChange(e, inputType.INPUT)}
              />
            </FlexContainer>
          </FilterContainer>
        )}

        {/* pricetype */}
        {instance === MarketplaceFilterCardEnum.MARKETPLACE && (
          <FilterContainer>
            <Label>Price Type</Label>
            <FlexContainer>
              {priceType.map((price, idx) => (
                <CRadioBox
                  key={idx}
                  label={price.title}
                  type={'checkbox'}
                  checked={modalFilter.priceType?.includes(price.id)}
                  name='priceType'
                  data-cy='priceType'
                  value={price.id}
                  onChange={(e) => handleValueChange(e, inputType.RADIO)}
                />
              ))}
            </FlexContainer>
          </FilterContainer>
        )}

        {/* price range */}
        {instance === MarketplaceFilterCardEnum.MARKETPLACE && (
          <FilterContainer>
            <Label>Price range</Label>
            <FlexContainer>
              <CInput
                type='number'
                name='priceMin'
                label='Min'
                hasValue={hasValue(modalFilter?.priceMin)}
                value={
                  Object.keys(modalFilter).length ? modalFilter?.priceMin : ''
                }
                onChange={(e) => handleValueChange(e, inputType.INPUT)}
              />
              <CInput
                type='number'
                name='priceMax'
                label='Max'
                hasValue={hasValue(modalFilter?.priceMax)}
                value={
                  Object.keys(modalFilter).length ? modalFilter?.priceMax : ''
                }
                onChange={(e) => handleValueChange(e, inputType.INPUT)}
              />
            </FlexContainer>
          </FilterContainer>
        )}

        {/* search */}
        {instance === MarketplaceFilterCardEnum.MARKETPLACE && (
          <FilterContainer>
            <SearchInput
              label='Search'
              name='search'
              hasValue={hasValue(modalFilter?.search)}
              value={Object.keys(modalFilter).length ? modalFilter?.search : ''}
              onChange={(e) => handleValueChange(e, inputType.INPUT)}
            />
          </FilterContainer>
        )}
      </FilterWrapper>
    </Dialog>
  );
};
